import type { Dataset, DatasetLabel, Embodiment, MemberRef } from "@/api";
import type { DatasetStorageBackend } from "@/api/datasetStorageBackend";
import { formatEmbodimentName, formatEndEffector } from "@/components/embodiment/embodimentDisplay";
import {
  formatArmLayout,
  formatRecordingMode,
  recordingModeGuide,
} from "@/components/filter/datasetFilters";
import { labelDisplayText, labelExplanation, sortLabelsForDisplay } from "@/components/label";
import { formatBytes } from "@/utils";
import { formatDuration } from "@/utils/datetime";
import { formatSchemaVersion } from "./datasetFormatters";

export type DatasetResourceKind = "dataset";

// 카드 우상단에 강조 표시하는 종합 상태. tone 은 색 의미(긍정/진행/중립/비활성)만 나타낸다.
// 실제 색은 DatasetCard CSS 가 tone 별로 매핑한다.
export type DatasetResourceStatusTone = "positive" | "progress" | "neutral" | "muted";

export type DatasetResourceStatus = {
  label: string;
  tone: DatasetResourceStatusTone;
  title?: string;
};

export type DatasetResourceBadge = {
  label: string;
  title?: string;
};

// 카드 메타 레이아웃 폭. full 은 한 줄 전체를 쓴다(기본). half 는 반폭이며 한 줄에 둘씩 놓는다.
export type DatasetResourceMetaSpan = "full" | "half";

export type DatasetResourceMetaItem = {
  label: string;
  value: string;
  title?: string;
  span?: DatasetResourceMetaSpan;
};

export type DatasetResourceChip = {
  color: number;
  label: string;
  title?: string;
};

export type DatasetResourceCardModel = {
  badges: readonly DatasetResourceBadge[];
  // 저장 위치. 기능 제약이 있는 위치만 카드가 칩으로 표시한다(`datasetStorageBadge`). 정보가 없는
  // 경로는 null 이다.
  storageBackend: DatasetStorageBackend | null;
  chips: readonly DatasetResourceChip[];
  // 라벨이 아닌 파생 축 칩(현재는 수집 방식뿐). 라벨 칩 개수 제한과 관계없이 항상 표시한다.
  // 필수 라벨 3개가 모두 있는 데이터셋에서는 라벨 예산 안에 이 칩의 자리가 없기 때문이다.
  derivedChips: readonly DatasetResourceChip[];
  kind: DatasetResourceKind;
  // 우상단 강조 상태. 상태 개념이 없는 리소스는 null 이다.
  status: DatasetResourceStatus | null;
  metaItems: readonly DatasetResourceMetaItem[];
  title: string;
};

export type DatasetCardDatasetLike = {
  display_name: string;
  // embodiment 개요(이름·EE)를 카드 메타에 표시한다. Dataset 응답의 필수 필드다.
  embodiment: Embodiment;
  // 팔 구성은 카탈로그가 아니라 데이터셋의 실제 채널로 판정한다. 정보가 없는 경로(리뷰 큐)는 지정하지 않는다.
  arm_layout?: Dataset["arm_layout"];
  labels: readonly DatasetLabel[];
  modified_at: string;
  // 담당자 Member 참조(uuid + 표시명). 지정하지 않았거나 정보가 없는 경로(리뷰 큐)는 null 이다.
  operator_member: MemberRef | null;
  schema_version: string;
  total_episodes?: number;
  // 길이·용량 지표(목록 표와 같은 축). null 은 집계하지 않은 값이므로 0 과 구분해 대시로 표시한다.
  median_episode_length_sec?: number | null;
  total_duration_sec?: number | null;
  total_size_bytes?: number | null;
  // 수집 방식 파생 칩의 입력. 라벨이 아니라 구조에서 파생한 컬럼이다. 두 값을 함께 봐야
  // 추론 롤아웃을 teleop 으로 잘못 표시하지 않는다(formatRecordingMode 가 두 값을 합친다).
  recording_mode?: Dataset["recording_mode"];
  data_origin?: Dataset["data_origin"];
  storage_backend?: DatasetStorageBackend;
};

export function createDatasetCardModel(dataset: DatasetCardDatasetLike): DatasetResourceCardModel {
  return {
    badges: [],
    storageBackend: dataset.storage_backend ?? null,
    chips: createDatasetResourceChips(dataset.labels),
    derivedChips: createRecordingModeChips(dataset.recording_mode, dataset.data_origin),
    kind: "dataset",
    // 상태 pill(Available/Archived)은 카드에 표시하지 않는다. 우상단은 행 액션([...]) 자리다.
    status: null,
    // 순서: Embodiment 한 줄 → 에피소드·용량 → 길이 중앙값·총 길이 → 버전·담당자.
    // 지표 6개는 반폭 2열로 묶어 카드 높이를 줄인다. Updated 는 그리드 카드에서 제외한다.
    metaItems: [
      embodimentMetaItem(dataset),
      { label: "에피소드", value: formatEpisodeCount(dataset.total_episodes), span: "half" },
      { label: "용량", value: formatBytes(dataset.total_size_bytes ?? null, "—"), span: "half" },
      {
        label: "길이 중앙값",
        value: formatSecondsDuration(dataset.median_episode_length_sec),
        span: "half",
      },
      { label: "총 길이", value: formatSecondsDuration(dataset.total_duration_sec), span: "half" },
      { label: "버전", value: formatSchemaVersion(dataset.schema_version), span: "half" },
      { label: "담당자", value: dataset.operator_member?.display_name ?? "-", span: "half" },
    ],
    title: dataset.display_name,
  };
}

// 개요 메타 한 줄. 이름 · 팔 구성 · EE 를 잇는다(목록 테이블의 통합 컬럼과 같은 축).
// 값이 없는 축은 뺀다. 좁은 카드에서는 말줄임되므로 title 에 전체 값을 둔다.
function embodimentMetaItem(dataset: DatasetCardDatasetLike): DatasetResourceMetaItem {
  const joined = [
    formatEmbodimentName(dataset.embodiment),
    formatArmLayout(dataset.arm_layout),
    dataset.embodiment.end_effector?.name
      ? formatEndEffector(dataset.embodiment.end_effector)
      : null,
  ]
    .filter((part) => part !== null && part !== "")
    .join(" · ");
  return { label: "Embodiment", value: joined, title: joined };
}

// 초 단위 지표를 표와 같은 formatDuration(ms) 규칙으로 그린다. 집계하지 않은 값(null, 미제공)은 대시다.
function formatSecondsDuration(seconds: number | null | undefined): string {
  return formatDuration(seconds == null ? null : seconds * 1000);
}

// 라벨 칩. 필수 라벨을 앞으로 정렬해 카드의 3칸 예산에 먼저 넣는다.
// 문구는 labelDisplay 규칙("출처: 알바")을 따른다.
export function createDatasetResourceChips(labels: readonly DatasetLabel[]): DatasetResourceChip[] {
  return sortLabelsForDisplay(labels).map((label) => {
    const text = labelDisplayText(label.key, label.value);
    return {
      color: label.color,
      label: text,
      // 카드는 툴팁이 있는 HatLabelChip 이 아니라 c6 LabelChip 을 쓴다(요약 표면 규칙).
      // 설명 문구가 있으면 native title 로 값의 의미를 제공한다.
      title: labelExplanation(label) ?? text,
    };
  });
}

// 수집 방식(recording_mode) 파생 칩. 라벨이 아니므로 서버 색이 없다. 그래서 c6 중립색(0)을 쓴다.
// 칩 텍스트에 축 이름을 붙이지 않는다. 값 문구("Direct Teaching (DT)")가 이미 축을 설명한다.
// "수집 방식: " 접두어도 넣으면 small 칩 폭을 넘는다. 축 이름은 title 에 둔다.
function createRecordingModeChips(
  recordingMode: Dataset["recording_mode"] | undefined,
  dataOrigin: Dataset["data_origin"] | undefined,
): DatasetResourceChip[] {
  const label = formatRecordingMode(recordingMode, dataOrigin);
  if (label === null) {
    return [];
  }
  const guide = recordingModeGuide(recordingMode, dataOrigin);
  return [
    { color: 0, label, title: guide === null ? `수집 방식: ${label}` : `수집 방식: ${guide}` },
  ];
}

function formatEpisodeCount(value: number | undefined): string {
  return value == null ? "-" : new Intl.NumberFormat(undefined).format(value);
}
